import { useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import FormatAddress from "go-core/components/Formatters/FormatAddress";
import FormatDate from "go-core/components/Formatters/FormatDate";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { ReactComponent as QRCodeSvg } from "../../../../../../../../../../../images/svg/gopos-qr-code.svg";
import { apiOrganization } from "../../../../../../../../../../../services/Api/Organization/apiOrganization";
import {
	CompanyInfoApi,
	PrintoutAdditionsPrintMethod,
	ReceiptApi,
} from "../../../../../../../../../../../services/Api/Organization/types";

interface Props {
	newAvatar?: string;
	form: UseFormReturn<ReceiptApi>;
	imageFetched: boolean;
}

const ReceiptTemplatePreview = ({ newAvatar, form, imageFetched }: Props) => {
	const [companyInfo, setCompanyInfo] = useState<CompanyInfoApi | undefined>(undefined);
	const organization = useSelector(selectOrganization);
	const { watch } = form;
	const showEmployee = watch("print_employee");
	const currency = organization?.currency || "";
	const { t } = useTranslation();
	const { addFlash } = useFlash();

	useEffect(() => {
		fetchCompanyInfo();
	}, []);

	const fetchCompanyInfo = async () => {
		try {
			const res = await apiOrganization.getCompanyInfo();
			setCompanyInfo(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	return (
		<div className="col-md-4">
			<div className="receipt mb-2">
				<div className="receipt-list">
					<div className="receipt-header">
						<div className="receipt-content-header">
							<div className="d-flex mb-4">
								{newAvatar && imageFetched && (
									<img
										id="printout-logo"
										className="logo-image"
										src={newAvatar}
										alt="Receipt image"
									/>
								)}
							</div>
							<div className="company-header">
								{watch("print_organization") && <h5>{organization.name}</h5>}
								{watch("print_company") && (
									<h6>
										{companyInfo && companyInfo.company_name
											? companyInfo.company_name
											: t("modules.receipt.field.company_name.title")}
									</h6>
								)}
								{watch("print_address") && (
									<div className="mb-2" style={{ marginTop: "-8px" }}>
										<FormatAddress
											address={{
												city: companyInfo?.address_city,
												street: companyInfo?.address_street,
												build_nr: companyInfo?.address_build_nr,
												flat_nr: companyInfo?.address_flat_nr,
												zip_code: companyInfo?.address_zip_code,
											}}
										/>
									</div>
								)}
								{watch("custom_header") && <h6>{watch("custom_header")}</h6>}
							</div>
							<div className="company-details">
								<span>
									{organization.more?.address?.street} {organization.more?.address?.build_nr}
									{`${
										organization.more?.address?.flat_nr
											? `/${organization.more?.address.flat_nr}`
											: ""
									}`}
								</span>
								<span>
									{organization.more?.address?.zip_code} {organization.more?.address?.city}
								</span>
								<span>
									{organization.more?.tax_id_no && (
										<>
											{t("lib:common.word.tax_id_no.title")}: {organization.more?.tax_id_no}
										</>
									)}
								</span>
							</div>
						</div>
						<div className="receipt-content-footer">
							<span>
								{t("modules.receipt.field.day_short.title")}{" "}
								{FormatDate(new Date().toLocaleDateString())}
							</span>
							<span>{t("modules.receipt.field.printout.title")} 0000001</span>
						</div>
					</div>
					<div className="receipt-content">
						<div className="receipt-content-header">
							<h6>{t("modules.receipt.field.fiscal_receipt.title")}</h6>
						</div>
						<div className="receipt-content-body">
							<div className="receipt-content-body-item">
								{watch("print_discount") ? (
									<>
										<div className="receipt-position">
											<span>Coca Cola 0.85l</span>
											<span>
												{FormatMoney({
													amount: 10,
													currency,
												})}
											</span>
										</div>
										<div className="receipt-position">
											<span className="ms-4">{t("modules.receipt.field.discount.title")}</span>
											<span>
												{FormatMoney({
													amount: -4,
													currency,
												})}
											</span>
										</div>
										<div className="receipt-position">
											<span className="ms-auto">
												{FormatMoney({
													amount: 6,
													currency,
												})}
											</span>
										</div>
									</>
								) : (
									<div className="receipt-position">
										<span>Coca Cola 0.85l</span>
										<span>
											{FormatMoney({
												amount: 6,
												currency,
											})}
										</span>
									</div>
								)}
								{watch("additions_print_method") === PrintoutAdditionsPrintMethod.AS_SEPARATE_ITEMS && (
									<>
										<div className="receipt-position">
											<span>Pizza Margherita</span>
											<span>
												{FormatMoney({
													amount: 30,
													currency,
												})}
											</span>
										</div>
										<div className="receipt-position">
											<span>{t("modules.receipt.field.ham.title")}</span>
											<span>
												{FormatMoney({
													amount: 3,
													currency,
												})}
											</span>
										</div>
										<div className="receipt-position">
											<span>{t("modules.receipt.field.mushroom.title")}</span>
											<span>
												{FormatMoney({
													amount: 3,
													currency,
												})}
											</span>
										</div>
									</>
								)}
								{watch("additions_print_method") === PrintoutAdditionsPrintMethod.IN_DESCRIPTION && (
									<>
										<div className="receipt-position">
											<span>Pizza Margherita</span>
											<span>
												{FormatMoney({
													amount: 36,
													currency,
												})}
											</span>
										</div>
										<div className="ms-1 text-muted">
											{t("modules.receipt.field.ham.title")},{" "}
											{t("modules.receipt.field.mushroom.title")}
										</div>
									</>
								)}
								{watch("additions_print_method") === PrintoutAdditionsPrintMethod.NO_ADDITIONS && (
									<div className="receipt-position">
										<span>Pizza Margherita</span>
										<span>
											{FormatMoney({
												amount: 36,
												currency,
											})}
										</span>
									</div>
								)}
							</div>
							<div className="receipt-content-body-item">
								<div className="receipt-position">
									<span>{t("common.word.tax")} 5%</span>
									<span>{FormatMoney({ amount: 2.1, currency })}</span>
								</div>
								<div className="receipt-position">
									<span>
										{t("common.word.tax")} {t("modules.receipt.field.sum.title")}
									</span>
									<span>{FormatMoney({ amount: 2.1, currency })}</span>
								</div>
							</div>
							<div className="receipt-content-body-item">
								<div className="receipt-position">
									<strong>{t("modules.receipt.field.sum.title").toUpperCase()}</strong>
									<strong>
										{FormatMoney({
											amount: 42,
											currency,
										})}
									</strong>
								</div>
							</div>
						</div>
						<div className="receipt-content-desc">
							<div className="desc-item">
								{showEmployee && (
									<>
										<div className="employee">
											<span>{t("common.word.employee")}</span>
											<span>Jan Kowalski</span>
										</div>
									</>
								)}
								{watch("print_comment") && (
									<span className="d-flex justify-content-center">
										{t("modules.receipt.field.example_bill_comment.title")}
									</span>
								)}
							</div>
						</div>
						<div>
							{(watch("facebook") || watch("instagram") || watch("website")) && (
								<div className="receipt-content-footer">
									<div className="receipt-content-footer-item">
										{watch("facebook") && <span>fb.com/{watch("facebook")}</span>}
										{watch("instagram") && <span>instagram.com/{watch("instagram")}</span>}
										{watch("website") && <span>{watch("website")}</span>}
									</div>
								</div>
							)}
							{(watch("custom_fiscal_text") || watch("custom_text")) && (
								<div className="receipt-content-footer">
									<div className="receipt-content-footer-item">
										{watch("custom_text") && <span>{watch("custom_text")}</span>}
										{watch("custom_fiscal_text") && <span>{watch("custom_fiscal_text")}</span>}
									</div>
								</div>
							)}
							{watch("print_qr_code") && (
								<div className="receipt-content-footer">
									<div className="receipt-content-footer-item">
										<QRCodeSvg />
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ReceiptTemplatePreview;
